import { useState } from "react";
import type { AuthenticatedAccount } from "./App";
import { AppShell } from "./AppShell";
import { enMessages } from "./messages";
import type { AppMessages } from "./messages";
import type { AppView } from "./navigation";

export interface PhaseClosedViewProps {
  account?: AuthenticatedAccount | null;
  messages?: AppMessages;
  activeView: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => Promise<void> | void;
  /** Which destination is closed, said as a heading. */
  title: string;
  /** Why it is closed and what its absence does *not* mean. */
  body: string;
}

/**
 * The honest rendering of a destination that is switched off for this phase.
 *
 * It exists so a closed feature is never a permanently empty list: an empty list
 * asserts "there is nothing", and while the thing that fills it is not running
 * that assertion is false. The shell stays mounted, so the person is not
 * stranded — every destination that *is* open is one click away in the rail.
 *
 * A heading and a sentence are the whole page, so neither gets a box: an outline
 * around one static statement would draw a group that has no second member.
 */
export function PhaseClosedView({
  account = null,
  messages = enMessages,
  activeView,
  onNavigate,
  onSignOut,
  title,
  body,
}: PhaseClosedViewProps) {
  const [signingOut, setSigningOut] = useState(false);

  const signOut = () => {
    if (!onSignOut || signingOut) return;
    setSigningOut(true);
    void Promise.resolve(onSignOut()).finally(() => setSigningOut(false));
  };

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={onSignOut && signOut}
      signingOut={signingOut}
    >
      <div className="page-stack">
        <div className="page-intro">
          <h1>{title}</h1>
          <p>{body}</p>
        </div>
      </div>
    </AppShell>
  );
}
